import { Link } from 'react-router-dom';
import type { EnvironmentHealth, RepositoryAccess } from '../../api';
import { Callout } from '../primitives';
import { targetPath, type SectionPath } from '../project/operationalHealth';

/**
 * DeployReadiness is the safety context at the top of the deploy dialog
 * (UX-07): what the server already knows about the environment. Items the
 * server marks blocks_deploy (the agent is gone, setup failed) stop a live
 * deployment; the rest are said once, as warnings. Repository access says
 * which credential will read the code (UX-13). Nothing is re-derived here.
 */
export function DeployReadiness({
  health,
  repositoryAccess,
  sectionPath,
  environmentName,
}: {
  health: EnvironmentHealth | null | undefined;
  repositoryAccess: RepositoryAccess | null | undefined;
  sectionPath?: SectionPath;
  environmentName: string;
}) {
  const attention = health?.attention ?? [];
  const blocking = attention.filter((item) => item.blocks_deploy);
  const warnings = attention.filter((item) => !item.blocks_deploy);
  const link = (item: (typeof attention)[number]) =>
    item.action && sectionPath ? (
      <Link to={targetPath(item.action.target, sectionPath, item.environment_id)}>{item.action.label}</Link>
    ) : null;

  return (
    <div className="deploy-readiness" data-testid="deploy-readiness">
      {blocking.length > 0 ? (
        <Callout tone="danger" title={`Deploying to ${environmentName} cannot work right now`} testId="deploy-blocking">
          <ul className="deploy-readiness__list">
            {blocking.map((item) => (
              <li key={item.id}>
                <strong>{item.title}.</strong> {item.summary} {link(item)}
              </li>
            ))}
          </ul>
          <p className="muted">A rehearsal is still possible: it touches nothing.</p>
        </Callout>
      ) : null}
      {warnings.length > 0 ? (
        <Callout tone="warning" role="status" title={blocking.length > 0 ? 'Also needs attention' : 'Needs attention'} testId="deploy-warnings">
          <ul className="deploy-readiness__list">
            {warnings.slice(0, 4).map((item) => (
              <li key={item.id}>
                {item.title}. {link(item)}
              </li>
            ))}
            {warnings.length > 4 ? <li className="muted">and {warnings.length - 4} more on the Overview.</li> : null}
          </ul>
        </Callout>
      ) : null}
      {repositoryAccess ? (
        <p className={`deploy-readiness__access${repositoryAccess.source === 'none' ? ' deploy-readiness__access--none' : ''}`} data-testid="deploy-repository-access">
          <strong>Repository access:</strong> {repositoryAccess.summary}
          {repositoryAccess.other ? ` ${repositoryAccess.other}` : ''}{' '}
          {sectionPath ? <Link to={sectionPath('repositories')}>{repositoryAccess.source === 'none' ? 'Set up access' : 'Change'}</Link> : null}
        </p>
      ) : null}
    </div>
  );
}
